import { useState } from 'react'
import { ChevronDown, FileSpreadsheet, FileText, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  csvExportFilename,
  unicodeFilenameStem,
  xlsxExportFilename,
} from '@digital-twins/shared-types'
import { publicApi, isGuestSessionExpiredError } from '@/lib/api/client'

type ResponseExportFormat = 'xlsx' | 'csv'

interface DownloadResponseExportButtonProps {
  /** Owner-side download; omit when downloading through a share link. */
  studyId?: string
  /** Share link the guest is viewing; routes the download through `/public`. */
  shareToken?: string
  /** Used for the saved filename; falls back to `response_export.<format>`. */
  studyName?: string
  /**
   * The study's current analysis has its scale tab book (SS-6): the button
   * becomes a menu, "Raw data" and "Tables". Studies analysed before the tab
   * book existed keep the single raw export button.
   */
  hasScaleTables?: boolean
  studyWorkbookStatus?: 'legacy' | 'building' | 'ready' | 'failed'
  variant?: 'outline' | 'ghost'
  className?: string
  'data-testid'?: string
  format: ResponseExportFormat
}

type DownloadKind = 'raw' | 'tables' | 'combined' | 'csv'

/** A Unicode-safe study-name file name, or the fallback when no name survives. */
function studyFilename(suffix: string, fallbackName: string) {
  return (studyName: string | undefined) => {
    const stem = unicodeFilenameStem(studyName)
    return stem ? `${stem}${suffix}` : fallbackName
  }
}

/** Per download: the owner path, the share download, the file name and its missing-file copy. */
const DOWNLOADS: Record<
  DownloadKind,
  {
    path: string
    shared: (shareToken: string) => Promise<Blob>
    filename: (studyName: string | undefined) => string
    unavailableKey: 'studyReport.exportUnavailable' | 'studyReport.tablesUnavailable'
  }
> = {
  raw: {
    path: 'export/xlsx',
    shared: (shareToken) => publicApi.downloadExportXlsx(shareToken),
    filename: xlsxExportFilename,
    unavailableKey: 'studyReport.exportUnavailable',
  },
  tables: {
    path: 'export/scale-tables.xlsx',
    shared: (shareToken) => publicApi.downloadScaleTablesXlsx(shareToken),
    filename: studyFilename('_tables.xlsx', 'scale-tables.xlsx'),
    unavailableKey: 'studyReport.tablesUnavailable',
  },
  combined: {
    path: 'export/data-and-tables.xlsx',
    shared: (shareToken) => publicApi.downloadCombinedWorkbookXlsx(shareToken),
    filename: studyFilename('_data_and_tables.xlsx', 'study-data-and-tables.xlsx'),
    unavailableKey: 'studyReport.exportUnavailable',
  },
  csv: {
    path: 'export/csv',
    shared: (shareToken) => publicApi.downloadExportCsv(shareToken),
    filename: csvExportFilename,
    unavailableKey: 'studyReport.exportUnavailable',
  },
}

/**
 * Downloads a study's response export and, when the study's current analysis
 * has one, its scale tab book.
 *
 * The raw bytes come from `GET /api/experiments/:id/export/xlsx`, which streams
 * the `response_export.xlsx` artifact written by the Argo `generate-export`
 * step. That step runs for every study whose simulation succeeded — full and
 * data-mode alike — so this control is safe to render on any completed study.
 * CSV streams `response_export.csv` from that same step, or the `data` sheet
 * of the xlsx when an older run never wrote a CSV.
 * The tables come from `GET /api/experiments/:id/export/scale-tables.xlsx`,
 * written beside the analysis when it was saved. On a share link there is no
 * session cookie, so `shareToken` routes the same artifacts through the
 * guest-authenticated public endpoints instead.
 */
function DownloadResponseExportButton({
  studyId,
  shareToken,
  studyName,
  hasScaleTables = false,
  studyWorkbookStatus = 'legacy',
  variant = 'outline',
  className,
  format,
  'data-testid': testIdProp,
}: DownloadResponseExportButtonProps) {
  const { t } = useTranslation('app')
  const testId = testIdProp ?? (format === 'csv' ? 'download-csv' : 'download-xlsx')
  const [isDownloading, setIsDownloading] = useState(false)

  async function download(kind: DownloadKind) {
    if ((!studyId && !shareToken) || isDownloading) return
    const spec = DOWNLOADS[kind]
    setIsDownloading(true)
    try {
      let blob: Blob
      if (shareToken) {
        try {
          blob = await spec.shared(shareToken)
        } catch (err) {
          // An expired guest session reopens the OTP gate on its own; a toast
          // on top of that gate would just be noise.
          if (!isGuestSessionExpiredError(err)) {
            toast.error(t(spec.unavailableKey))
          }
          return
        }
      } else {
        const response = await fetch(`/api/experiments/${studyId}/${spec.path}`, {
          credentials: 'include',
        })
        if (!response.ok) {
          toast.error(t(spec.unavailableKey))
          return
        }
        blob = await response.blob()
      }
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = spec.filename(studyName)
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch {
      toast.error(t('studyReport.exportFailed'))
    } finally {
      setIsDownloading(false)
    }
  }

  const icon = isDownloading ? (
    <Loader2 className="w-4 h-4 animate-spin" />
  ) : format === 'csv' ? (
    <FileText className="w-4 h-4" />
  ) : (
    <FileSpreadsheet className="w-4 h-4" />
  )

  if (format === 'csv') {
    return (
      <Button
        variant={variant}
        size="sm"
        onClick={() => download('csv')}
        disabled={isDownloading}
        data-testid={testId}
        className={className}
      >
        {icon}
        {t('studyReport.downloadCsv')}
      </Button>
    )
  }

  if (studyWorkbookStatus !== 'legacy') {
    return (
      <Button
        variant={variant}
        size="sm"
        onClick={() => {
          if (studyWorkbookStatus === 'ready' || studyWorkbookStatus === 'building') {
            void download('combined')
          } else {
            toast.error(t('studyReport.exportUnavailable'))
          }
        }}
        disabled={isDownloading}
        data-testid={testId}
        className={className}
      >
        {icon}
        {t('studyReport.downloadXlsx')}
      </Button>
    )
  }

  if (!hasScaleTables) {
    return (
      <Button
        variant={variant}
        size="sm"
        onClick={() => download('raw')}
        disabled={isDownloading}
        data-testid={testId}
        className={className}
      >
        {icon}
        {t('studyReport.downloadXlsx')}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size="sm"
          disabled={isDownloading}
          data-testid={testId}
          className={className}
        >
          {icon}
          {t('studyReport.downloadXlsx')}
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="download-raw-xlsx" onSelect={() => download('raw')}>
          {t('studyReport.downloadRawData')}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="download-scale-tables-xlsx"
          onSelect={() => download('tables')}
        >
          {t('studyReport.downloadTables')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type DownloadButtonProps = Omit<DownloadResponseExportButtonProps, 'format'>

export function DownloadXlsxButton(props: DownloadButtonProps) {
  return <DownloadResponseExportButton format="xlsx" {...props} />
}

export function DownloadCsvButton(props: DownloadButtonProps) {
  return <DownloadResponseExportButton format="csv" {...props} />
}
